import { useState } from 'react';
import { Skeleton, Space } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { ErrorDiv, ErrorLabel, StyledInput } from 'components/new-ui/composed/formInput/styles';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import useLastEmail, { EMAIL_REGEX } from './useLastEmail';
import RemoveJobFromEmailNotificationModal from './RemoveJobFromEmailNotificationModal';
import { getEmailJobsDocuments, sendJobsEmailToCustomer } from 'store/jobs/client';
import CollapsibleJob from './CollapsibleJob';
import { useRequestPromise } from 'hooks/useRequest';
import { StyledList } from './styles';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';

type Props = {
    open: boolean;
    manifestId: string;
    selectedJobIds: string[];
    onClose: () => void;
    onSuccess: () => void;
};

const EmailJobModal = ({ open, manifestId, selectedJobIds, onClose, onSuccess }: Props) => {
    const { data, isLoading } = useRequestPromise(
        () => {
            if (!open) return null;
            return getEmailJobsDocuments(manifestId, selectedJobIds);
        },
        {},
        [open]
    );

    const { lastEmail, updateLastEmail } = useLastEmail();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [email, setEmail] = useState<string>();
    const [touched, setTouched] = useState(false);
    const [jobIdToRemove, setJobIdToRemove] = useState<string | undefined>(undefined);
    const [showRemoveJobConfirmationModal, setShowRemoveJobConfirmationModal] = useState(false);
    const [removedJobIds, setRemovedJobIds] = useState<Set<string>>(new Set());

    const effectiveEmail = email ?? lastEmail ?? '';
    const emailError = !effectiveEmail
        ? 'Email is required.'
        : !EMAIL_REGEX.test(effectiveEmail)
        ? 'Invalid email.'
        : null;
    const activeJobs = data?.jobs.filter((j) => !removedJobIds.has(j.jobId)) ?? [];
    const allJobsHaveFiles = activeJobs.every((job) => job.summary.availableFileCount > 0);
    const disabledSubmit = Boolean(emailError) || !allJobsHaveFiles || activeJobs.length < 1;

    const handleSendEmail = async () => {
        const activeJobIds = activeJobs.map((j) => j.jobId);

        // We want to preserve the last successfully submitted email from the form
        updateLastEmail(email);

        showGlobalOverlay({ type: 'LoadingOverlay' });

        try {
            const response = await sendJobsEmailToCustomer(manifestId, activeJobIds, effectiveEmail);
            const messageTitle = response.requestedJobCount > 1 ? 'Jobs Emailed' : 'Job Emailed';
            const messageDescription =
                response.requestedJobCount > 1
                    ? 'The jobs have been successfully emailed.'
                    : 'The job has been successfully emailed.';

            Notification({
                type: 'success',
                messageTitle: messageTitle,
                description: messageDescription,
            });

            onSuccess();
            resetState();
        } catch (error) {
            const errorMessage = getErrorMessage(error) ?? DEFAULT_INTERNAL_ERROR_MESSAGE;

            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage,
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleRemoveJob = (jobId: string) => {
        setRemovedJobIds((prev) => new Set(prev).add(jobId));
    };

    const resetState = () => {
        setTouched(false);
        setJobIdToRemove(undefined);
        setShowRemoveJobConfirmationModal(false);
        setRemovedJobIds(new Set());
    };

    return (
        <>
            <ConfirmationModal
                title="Email Job"
                confirmButtonText="Send"
                icon={<MailOutlined />}
                open={open}
                confirmationButtonDisabled={disabledSubmit}
                onConfirm={handleSendEmail}
                onClose={() => {
                    onClose();
                    resetState();
                }}
                onCancel={() => {
                    onClose();
                    resetState();
                }}
                content={
                    <>
                        <div style={{ width: '100%', marginTop: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'start', gap: 90 }}>
                                <Space size={4} style={{ paddingTop: 6 }}>
                                    <LabelRequiredSymbol />
                                    <label htmlFor="email">To</label>
                                    <span>:</span>
                                </Space>

                                <div style={{ width: '100%' }}>
                                    <StyledInput
                                        value={effectiveEmail}
                                        type="email"
                                        id="email"
                                        placeholder="Enter email address"
                                        showSuccess={true}
                                        status={touched && emailError ? 'error' : undefined}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onBlur={() => setTouched(true)}
                                    />

                                    {touched && emailError && (
                                        <ErrorDiv error touched>
                                            <ErrorLabel>{emailError}</ErrorLabel>
                                        </ErrorDiv>
                                    )}
                                </div>
                            </div>
                        </div>
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 2 }} />
                        ) : (
                            <StyledList>
                                {activeJobs.map((job) => (
                                    <CollapsibleJob
                                        key={job.jobId}
                                        job={job}
                                        onRemoveJobFromEmail={(jobId) => {
                                            setJobIdToRemove(jobId);
                                            setShowRemoveJobConfirmationModal(true);
                                        }}
                                    />
                                ))}
                            </StyledList>
                        )}
                    </>
                }
            />

            <RemoveJobFromEmailNotificationModal
                open={showRemoveJobConfirmationModal}
                onClose={() => setShowRemoveJobConfirmationModal(false)}
                onConfirm={() => {
                    if (jobIdToRemove) handleRemoveJob(jobIdToRemove);
                    setShowRemoveJobConfirmationModal(false);
                    setJobIdToRemove(undefined);
                }}
            />
        </>
    );
};

export default EmailJobModal;
